/**
 * Category margin rules (#3034): one margin per category (and its subcategories), read by everyone who may
 * read inventory and managed with `inventory.pricing.manage`. A margin is on the SELLING price, never a markup.
 * A rule changes no price by itself; prices change only through an explicit recalculation (see CategoryRecalculation).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/stores/notificationStore';
import {
  flattenPages,
  useCategoryMarginRules,
  useDeleteCategoryRule,
  usePricingPermissions,
  usePutCategoryRule,
  type CategoryMarginRule,
} from '@/hooks/usePartPricing';
import { formatPercent, impliedMarkupOfMargin, percentInput, percentToInput } from './decimal';
import { PricingErrorAlert } from './feedback';
import { OwnedBoundary } from './OwnedBoundary';
import { isSessionCancel } from './pricingSession';
import { describePricingError } from './pricingErrors';
import { CategoryName, CategoryPicker } from './pickers';

interface RuleDialogProps {
  open: boolean;
  /** The rule being edited, or null to add one. */
  rule: CategoryMarginRule | null;
  /** Categories that already have a rule; picking one in add mode REPLACES that rule, and says so. */
  rulesByCategory: ReadonlyMap<string, CategoryMarginRule>;
  /** False while the rule pages are still loading: the replace check would be incomplete. */
  rulesComplete: boolean;
  onClose: () => void;
}

function CategoryRuleDialog({ open, rule, rulesByCategory, rulesComplete, onClose }: RuleDialogProps) {
  const { t, i18n } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const put = usePutCategoryRule();
  const [categoryId, setCategoryId] = useState<string | null>(rule?.categoryId ?? null);
  const [marginText, setMarginText] = useState(rule ? percentToInput(rule.marginPercent) : '');
  const [isActive, setIsActive] = useState(rule?.isActive ?? true);
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const parsed = percentInput(marginText, 'margin');
  const existing = !rule && categoryId ? rulesByCategory.get(categoryId) : undefined;
  const markup = parsed.ok ? impliedMarkupOfMargin(parsed.value, i18n.language) : null;
  const busy = put.isPending;

  const save = async () => {
    setShowErrors(true);
    if (!categoryId || !parsed.ok) return;
    setError(null);
    try {
      await put.mutateAsync({ categoryId, body: { marginPercent: parsed.value, isActive } });
      notify.success(t('rules.saved'));
      onClose();
    } catch (failure) {
      if (isSessionCancel(failure)) return;
      setError(failure);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      maxWidth="sm"
      fullWidth
      mobileTitle={rule ? t('rules.editTitle') : t('rules.addTitle')}
    >
      <DialogTitle>{rule ? t('rules.editTitle') : t('rules.addTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {rule ? (
            <Typography variant="body1">
              <CategoryName id={rule.categoryId} />
            </Typography>
          ) : (
            <CategoryPicker label={t('fields.category')} value={categoryId} onChange={setCategoryId} disabled={busy} />
          )}
          {existing && (
            <Alert severity="warning">
              {t('rules.replaceNotice', { margin: formatPercent(existing.marginPercent, i18n.language) })}
            </Alert>
          )}
          <TextField
            size="small"
            required
            label={t('fields.margin')}
            value={marginText}
            disabled={busy}
            onChange={(event) => setMarginText(event.target.value)}
            error={showErrors && !parsed.ok}
            helperText={showErrors && !parsed.ok ? t(`validation.margin.${parsed.error}`) : t('hints.marginDefinition')}
            slotProps={{
              htmlInput: { inputMode: 'decimal' },
              input: { endAdornment: <InputAdornment position="end">{t('units.percent')}</InputAdornment> },
            }}
          />
          {markup && <Typography variant="body2" color="text.secondary">{t('hints.impliedMarkup', { markup })}</Typography>}
          <FormControlLabel
            control={<Switch checked={isActive} disabled={busy} onChange={(event) => setIsActive(event.target.checked)} />}
            label={t('fields.ruleActive')}
          />
          {!rule && !rulesComplete && <Typography variant="caption" color="text.secondary">{t('rules.checkingExisting')}</Typography>}
          <PricingErrorAlert error={error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={busy || (!rule && !rulesComplete)}>
          {busy ? tc('saving') : existing ? t('rules.replace') : tc('save')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

function CategoryRulesPanelBody() {
  const { t, i18n } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { canManage } = usePricingPermissions();
  const rules = useCategoryMarginRules();
  const remove = useDeleteCategoryRule();
  const [dialog, setDialog] = useState<{ rule: CategoryMarginRule | null } | null>(null);
  const [deleting, setDeleting] = useState<CategoryMarginRule | null>(null);

  const rows = flattenPages(rules.data);
  const total = rules.data?.pages[rules.data.pages.length - 1]?.pagination.total ?? 0;
  const rulesByCategory = new Map(rows.map((row) => [row.categoryId, row] as const));
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = rules;
  const loadedPages = rules.data?.pages.length ?? 0;

  // The add dialog must know every existing rule, or it cannot tell "add" from "replace". A page that arrives
  // without an observable "fetching" render still moves `loadedPages`, so the chain of pages never stalls.
  useEffect(() => {
    if (dialog && !dialog.rule && hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [dialog, hasNextPage, isFetchingNextPage, fetchNextPage, loadedPages]);

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    try {
      await remove.mutateAsync(target.categoryId);
      notify.success(t('rules.deleted'));
    } catch (failure) {
      if (isSessionCancel(failure)) return;
      notify.error(t('rules.deleteFailed'), describePricingError(failure, t, tc));
    } finally {
      setDeleting((current) => (current?.id === target.id ? null : current));
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h6">{t('rules.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('rules.intro')}
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ rule: null })}>
            {t('rules.add')}
          </Button>
        )}
      </Stack>
      <Alert severity="info">{t('hints.marginDefinitionLong')}</Alert>

      {rules.isError && <PricingErrorAlert error={rules.error} />}
      {rules.isLoading && <Typography color="text.secondary">{tc('loading')}</Typography>}
      {!rules.isLoading && !rules.isError && rows.length === 0 && <Typography color="text.secondary">{t('rules.empty')}</Typography>}
      {rows.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label={t('rules.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('fields.category')}</TableCell>
                <TableCell align="right">{t('fields.margin')}</TableCell>
                <TableCell align="right">{t('fields.impliedMarkup')}</TableCell>
                <TableCell>{t('fields.state')}</TableCell>
                {canManage && <TableCell align="right">{tc('actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.categoryName}</TableCell>
                  <TableCell align="right">{formatPercent(row.marginPercent, i18n.language)}</TableCell>
                  <TableCell align="right">{impliedMarkupOfMargin(row.marginPercent, i18n.language) ?? ''}</TableCell>
                  <TableCell>
                    <Chip size="small" color={row.isActive ? 'success' : 'default'} label={row.isActive ? t('state.active') : t('state.disabled')} />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title={tc('edit')}>
                        <IconButton size="small" aria-label={`${tc('edit')}: ${row.categoryName}`} onClick={() => setDialog({ rule: row })}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={tc('delete')}>
                        <IconButton size="small" aria-label={`${tc('delete')}: ${row.categoryName}`} onClick={() => setDeleting(row)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {rows.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            {t('list.showing', { shown: rows.length, total })}
          </Typography>
          {rules.hasNextPage && (
            <Button size="small" onClick={() => void rules.fetchNextPage()} disabled={rules.isFetchingNextPage}>
              {rules.isFetchingNextPage ? tc('loading') : tc('loadMore')}
            </Button>
          )}
        </Stack>
      )}

      {dialog && (
        <CategoryRuleDialog
          key={dialog.rule?.id ?? 'new'}
          open
          rule={dialog.rule}
          rulesByCategory={rulesByCategory}
          rulesComplete={!rules.hasNextPage && !rules.isFetchingNextPage}
          onClose={() => setDialog(null)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title={t('rules.deleteTitle')}
        message={t('rules.deleteMessage', { category: deleting?.categoryName ?? '' })}
        confirmText={tc('delete')}
        confirmColor="error"
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!remove.isPending) setDeleting(null);
        }}
      />
    </Stack>
  );
}

export function CategoryRulesPanel() {
  return (
    <OwnedBoundary>
      <CategoryRulesPanelBody />
    </OwnedBoundary>
  );
}
